import axios from "axios";
import qs from "qs";
import { useAuthStore } from "../Contexts/AuthContext";
import {
  dispatchAuthUnauthorized,
  getAuthToken,
  assertAuthTokenNotExpired,
} from "../Services/authToken";
import { loadItem } from "../Utils";
import { API_URL } from "../config";
import { Endpoints } from "./Endpoints";

/** Prevent IB/BaaS proxy calls from leaving UI spinners stuck forever. */
const DEFAULT_REQUEST_TIMEOUT_MS = 90_000;

const fetchHttpClient = axios.create();
fetchHttpClient.interceptors.request.use(
  (config) => {
    assertAuthTokenNotExpired();
    return config;
  },
  (error) => Promise.reject(error),
);
fetchHttpClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      dispatchAuthUnauthorized();
    }
    return Promise.reject(error);
  },
);

const createHeaders = () => {
  const options = loadItem("Options");

  const headers = {
    "branch-id": options?.branch_id,
    Authorization: "",
  };

  // Simulate partner DNS host for API calls when the browser hostname is a WL alias.
  let devInstanceHost = "";
  if (typeof window !== "undefined") {
    const hostname = String(window.location.hostname || "").trim().toLowerCase();
    if (hostname && hostname !== "localhost" && hostname !== "127.0.0.1") {
      devInstanceHost = hostname;
    }
  }
  if (devInstanceHost) {
    // Local / IsDevelopment: MobileService honors X-Dev-Instance-Host.
    headers["X-Dev-Instance-Host"] = devInstanceHost;
    // Production cross-origin WL: shared MobileService Host is azurewebsites;
    // PublicBrandingController already prefers X-Forwarded-Host for DNS resolve.
    headers["X-Forwarded-Host"] = devInstanceHost;
  }

  const token = getAuthToken() || useAuthStore.getState().token;

  if (token) {
    headers.Authorization = `Bearer ${token.trim()}`;
  }
  return headers;
};

const AXIOS_RESPONSE_TYPES = new Set([
  "arraybuffer",
  "blob",
  "document",
  "json",
  "text",
  "stream",
]);

const resolveAxiosResponseType = (value) => {
  if (value == null || value === "") return "json";
  const normalized = String(value).toLowerCase();
  return AXIOS_RESPONSE_TYPES.has(normalized) ? normalized : "json";
};

export const fetchAxios = ({
  verb,
  context,
  responseType,
  queryParameters = null,
  id = null,
  afterID = null,
  body = null,
  cancelToken,
  additionalHeaders = {}
}) => {
  return new Promise((resolve, reject) => {
    const headers = { ...additionalHeaders, ...createHeaders() };
    const safeResponseType = resolveAxiosResponseType(responseType);

    let result = null;
    let valueId = "";
    let valueAfterId = "";
    let query = "";
    let bodyValues = {};

    if (
      id &&
      (typeof id === "string" ||
        typeof id === "number" ||
        typeof id === "boolean")
    ) {
      valueId = `/${id.toString()}`;
    }
    if (
      afterID &&
      (typeof afterID === "string" ||
        typeof afterID === "number" ||
        typeof afterID === "boolean")
    ) {
      valueAfterId = `/${afterID.toString()}`;
    }

    if (queryParameters != null && typeof queryParameters === "object") {
      const cleanedParams = Object.fromEntries(
        Object.entries(queryParameters).filter(
          ([, value]) => value !== "" && value !== null && value !== undefined
        )
      );
      query = qs.stringify(cleanedParams, {
        addQueryPrefix: true,
      });
    }

    const url = `${API_URL}${Endpoints[verb][context]}${valueId}${valueAfterId}${query}`;

    console.log(url);

    switch (verb) {
      case "GET":
        result = fetchHttpClient.get(url, {
          headers,
          responseType: safeResponseType,
          cancelToken,
          timeout: DEFAULT_REQUEST_TIMEOUT_MS,
        });
        break;
      case "PUT":
        result = fetchHttpClient.put(url, body, {
          headers,
          cancelToken,
          timeout: DEFAULT_REQUEST_TIMEOUT_MS,
        });
        break;
      case "POST":
        result = fetchHttpClient.post(url, body, {
          headers,
          cancelToken,
          timeout: DEFAULT_REQUEST_TIMEOUT_MS,
        });
        break;
      case "DELETE":
        result = fetchHttpClient.delete(url, {
          headers,
          cancelToken,
          timeout: DEFAULT_REQUEST_TIMEOUT_MS,
        });
        break;
      case "PATCH":
        result = fetchHttpClient.patch(url, body, {
          headers,
          cancelToken,
          timeout: DEFAULT_REQUEST_TIMEOUT_MS,
        });
        break;
      default:
        break;
    }

    result.then((data) => resolve(data)).catch((err) => reject(err));
  });
};
